import React, { useEffect, useState } from "react"
import { useFaceDetection, DetectionQuality } from "@/hooks/useFaceDetection"
import { cn } from "@/utils/utils"
import { Button } from "@/components/ui/button"
import { CheckCircle, Camera, Loader2, ShieldCheck, RotateCcw } from "lucide-react"
import { dataUrlToFile } from "@/utils/file"
import { useIsMobile } from "@/hooks/use-mobile"

interface FaceCaptureProps {
  onCapture: (file: File) => Promise<void>
}

export const FaceCapture: React.FC<FaceCaptureProps> = ({ onCapture }) => {
  const isMobile = useIsMobile()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    videoRef,
    detectionStatus,
    errorMessage,
    modelsLoaded,
    isRNWebView,
    nativeCameraImage,
    requestNativeCamera,
    clearNativeImage,
  } = useFaceDetection()

  const isGoodCapture = detectionStatus === "good"

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = "unset"
      }
    }
  }, [isMobile])

  // Captura a partir do stream de vídeo (navegador)
  const handleCapture = async () => {
    if (!isGoodCapture || !videoRef.current) return

    const video = videoRef.current
    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext("2d")

    if (context) {
      context.translate(canvas.width, 0)
      context.scale(-1, 1)
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      const dataUrl = canvas.toDataURL("image/jpeg")
      try {
        const file = dataUrlToFile(dataUrl, "face_capture.jpg")
        setIsSubmitting(true)
        await onCapture(file)
      } catch (err) {
        console.error(err)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  // Confirma a foto tirada pela câmera nativa (React Native)
  const handleNativeConfirm = async () => {
    if (!nativeCameraImage) return
    try {
      const file = dataUrlToFile(nativeCameraImage, "face_capture.jpg")
      setIsSubmitting(true)
      await onCapture(file)
    } catch (err) {
      console.error(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- UI do React Native WebView ---
  if (isRNWebView) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center gap-6 p-6">
        <header className="w-full space-y-2 text-center">
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="h-4 w-4 text-orange-500" />
            <span className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-neutral-400">
              Ambiente Seguro
            </span>
          </div>
          <h2 className="text-3xl font-extralight leading-tight tracking-tight text-white">
            Verificação <span className="font-bold text-orange-500">Facial</span>
          </h2>
          <p className="text-sm text-neutral-500">
            Tire uma foto do seu rosto para confirmar sua identidade.
          </p>
        </header>

        {nativeCameraImage ? (
          // Preview da foto capturada
          <>
            <div className="relative w-full overflow-hidden rounded-2xl border border-neutral-800">
              <img
                src={nativeCameraImage}
                alt="Foto capturada"
                className="w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
                <div className="h-full w-full max-w-[260px] rounded-[40%] border-2 border-orange-500/60" />
              </div>
            </div>

            <div className="flex w-full gap-3">
              <Button
                onClick={clearNativeImage}
                variant="outline"
                className="flex-1 rounded-2xl border-neutral-700 bg-transparent text-white hover:bg-neutral-800"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Repetir
              </Button>
              <Button
                onClick={handleNativeConfirm}
                disabled={isSubmitting}
                className="flex-1 rounded-2xl bg-orange-500 font-bold text-white hover:bg-orange-600 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                {isSubmitting ? "Processando..." : "Confirmar"}
              </Button>
            </div>
          </>
        ) : (
          // Botão para abrir a câmera nativa
          <Button
            onClick={requestNativeCamera}
            className="h-16 w-full rounded-2xl bg-orange-500 text-white shadow-[0_10px_40px_rgba(249,115,22,0.25)] hover:bg-orange-600"
          >
            <Camera className="mr-3 h-5 w-5" />
            <span className="text-xs font-black uppercase tracking-widest">Abrir Câmera</span>
          </Button>
        )}

        <p className="text-center text-[11px] uppercase leading-loose tracking-widest text-neutral-600">
          Criptografado ponta-a-ponta <br /> Protegido por Advanced Vision IA.
        </p>
      </div>
    )
  }

  // --- UI do Navegador (stream de vídeo + face-api.js) ---
  const StatusFeedback = () => (
    <div
      className={cn(
        "flex items-start gap-4 rounded-2xl border p-5 transition-all duration-300",
        isGoodCapture
          ? "border-emerald-500/20 bg-emerald-500/10 shadow-[0_0_15px_rgba(16,185,129,0.05)]"
          : "border-neutral-800 bg-neutral-900"
      )}
    >
      <div
        className={cn(
          "mt-0.5 flex-shrink-0 rounded-full p-2",
          isGoodCapture ? "bg-emerald-500 text-black" : "bg-neutral-800 text-neutral-500"
        )}
      >
        {isGoodCapture ? (
          <CheckCircle className="h-4 w-4" />
        ) : (
          <Loader2 className="h-4 w-4 animate-spin" />
        )}
      </div>
      <div className="space-y-1">
        <p
          className={cn("text-sm font-semibold", isGoodCapture ? "text-white" : "text-neutral-300")}
        >
          {isGoodCapture ? "Enquadramento Perfeito" : "Analisando..."}
        </p>
        <p className="text-xs leading-relaxed text-neutral-500">
          {errorMessage || "Centralize seu rosto e garanta boa iluminação."}
        </p>
      </div>
    </div>
  )

  const BotaoCaptura = ({ className }: { className?: string }) => (
    <Button
      onClick={handleCapture}
      disabled={!isGoodCapture || isSubmitting}
      className={cn(
        "relative h-14 overflow-hidden rounded-2xl text-base font-bold transition-all duration-500 lg:h-16",
        "bg-orange-500 text-white shadow-[0_10px_40px_rgba(249,115,22,0.25)] hover:bg-orange-600",
        "disabled:bg-neutral-800 disabled:text-neutral-600 disabled:opacity-50 disabled:shadow-none",
        className
      )}
    >
      <div className="flex items-center justify-center gap-3">
        {isSubmitting ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Camera className={cn("h-5 w-5", isGoodCapture && "animate-pulse")} />
        )}
        <span className="text-xs font-black uppercase tracking-widest">
          {isSubmitting ? "Processando..." : "Capturar Biometria"}
        </span>
      </div>
    </Button>
  )

  return (
    <div className="mx-auto w-full max-w-7xl p-4 md:p-6 lg:p-10">
      <div className="overflow-hidden rounded-3xl shadow-[0_20px_80px_rgba(0,0,0,0.7)] lg:rounded-[2.5rem]">
        <div className="flex flex-col lg:flex-row">
          <div className="relative mx-20 flex flex-1 overflow-hidden rounded-lg p-4 lg:p-0">
            <div
              style={isMobile ? { aspectRatio: "4/5" } : undefined}
              className={cn(
                "relative w-full overflow-hidden bg-[#080808] transition-all duration-700 ease-in-out",
                "border border-neutral-800 lg:border-none",
                "rounded-2xl lg:rounded-none",
                isMobile
                  ? "mx-auto max-w-sm shadow-2xl self-start"
                  : "aspect-video lg:aspect-square xl:aspect-video"
              )}
            >
              {!modelsLoaded && (
                <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#0C0C0C]">
                  <Loader2 className="h-10 w-10 animate-spin text-orange-500 opacity-60" />
                  <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-600">
                    Iniciando IA
                  </p>
                </div>
              )}

              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={cn(
                  "h-full w-full scale-x-[-1] object-cover transition-transform duration-1000 ease-out",
                  isGoodCapture ? "scale-[1.03]" : "scale-100"
                )}
              />

              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-6 lg:p-12">
                <div
                  className={cn(
                    "h-full w-full max-w-[400px] rounded-[40%] border-2 transition-all duration-500",
                    isGoodCapture
                      ? "border-green-500 shadow-[0_0_60px_rgba(249,115,22,0.2)_inset,0_0_30px_rgba(249,115,22,0.1)]"
                      : "border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.6)_inset]"
                  )}
                />
              </div>

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
            </div>
          </div>

          <div className="flex w-full flex-col justify-between rounded-3xl border border-neutral-800/50 bg-[#0F0F0F] p-6 md:p-8 lg:w-[420px] lg:border-l lg:border-t-0 lg:p-12">
            <div className="space-y-10 lg:space-y-12">
              <header className="space-y-3">
                <div className="flex items-center gap-2.5 text-orange-500/80">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-neutral-400">
                    Ambiente Seguro
                  </span>
                </div>
                <h2 className="text-3xl font-extralight leading-tight tracking-tight text-white md:text-4xl">
                  Verificação <br />
                  <span className="font-bold text-orange-500">Facial</span>
                </h2>
                <p className="max-w-sm text-sm text-neutral-500">
                  Esta etapa é necessária para confirmar sua identidade e garantir a segurança da
                  sua conta.
                </p>
              </header>

              <div className="space-y-4">
                <StatusFeedback />
              </div>
            </div>
            <div className="mt-4 space-y-6">
              {!isMobile && <BotaoCaptura className="w-full" />}
              <p className="text-center text-[11px] uppercase leading-loose tracking-widest text-neutral-600">
                Criptografado ponta-a-ponta <br /> Protegido por Advanced Vision IA.
              </p>
            </div>
          </div>
        </div>
      </div>

      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-neutral-800/50 bg-black p-4 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] backdrop-blur-sm">
          <BotaoCaptura className="mx-auto w-full max-w-md" />
        </div>
      )}
    </div>
  )
}

export default FaceCapture
